/**
 * Each phone's own side of a profile-to-profile exchange — the records a phone
 * writes for itself once two profile holders have connected. One home for both
 * flows: the old design writes at the knock (qrPXPExchange), the new design
 * only after both have tapped (pxpOffer, E3b-2a). Extracted from
 * qrPXPExchange.js unchanged; the room record itself (connections) is not
 * here — the old design's confirm calls and the new design's offer own it.
 */

import { generateMessageId } from '../../utils/messageUtils';
import { getProfileUserId, getAnonUserId } from '../../utils/identityUtils';
import { buildProfilePayload } from '../../utils/profilePayload';
import { firstNameOf } from '../../utils/contactUtils';
import { sendMessage } from '../../services/chat/chatService';
import { persistMessage } from '../../services/chat/messageStore';
import { getProfileData, saveConnectionMetadata } from '../../services/storage/userDataStorage';
import { buildSystemMeetingMessage } from '../../services/chat/messageBuilders';
import { logMeetingRepeatConnect } from '../../services/analytics/eventLogger';
import { formatSystemMessageLocation } from '../../utils/locationFormatters';
import { minutesSinceMeetingStart } from '../../services/meetings/meetingRule';
import { decideHostMeeting, meetingRecordFrom } from '../../services/meetings/meetingDecision';
import { applyRepeatConnect, getSystemMessageId } from '../../services/meetings/repeatConnect';
import { findExistingConnection } from '../../services/activeSession';
import { currentUtcOffsetMinutes } from '../../utils/dateHelpers';

/**
 * The host, on a knock — read-only: have these two met (both profile ids, plus
 * the scanner's pre-conversion guest id), and the meeting verdict, decided
 * here once for both phones (signup-meetings § The rule; the scanner adopts it).
 * @returns {Promise<{existingChatRoomId, verdict, pxpMetadata, pxpTimestamp}>}
 */
export const decideHostSide = async (
  payload,
  { profileUserId, locationDataRef, coordsPromiseRef }
) => {
  let existingChatRoomId = null;
  try {
    existingChatRoomId = await findExistingConnection(
      profileUserId,
      payload.senderProfileUserId,
      payload.senderAnonUserId || null
    );
    if (existingChatRoomId) {
      console.log(
        '[QR_SCREEN] PXP Host: Reconnection detected, existing chatRoomId:',
        existingChatRoomId
      );
    }
  } catch (err) {
    // Detection failed — fall through to the first-connection flow (as ever)
    console.warn(
      '[QR_SCREEN] PXP Host: Detection failed, falling through to first-connection flow:',
      err
    );
  }
  const pxpTimestamp = new Date().toISOString();
  const verdict = await decideHostMeeting({
    existingChatRoomId,
    profileUserId,
    coordsPromise: coordsPromiseRef?.current || null,
    nowIso: pxpTimestamp,
    nowUtcOffset: currentUtcOffsetMinutes(),
  });
  console.log(`[QR_SCREEN] PXP Host: meeting verdict ${verdict.kind}`);
  const pxpMetadata = meetingRecordFrom(verdict, {
    exchangeType: 'pxp',
    locationData: locationDataRef.current,
    nowIso: pxpTimestamp,
  });
  return { existingChatRoomId, verdict, pxpMetadata, pxpTimestamp };
};

/**
 * The host's records: the scanner's card, the meeting record and the meeting
 * banner (first meeting), or the repeat-connect merge (reconnect / re-meet).
 */
export const writeHostRecord = async ({
  isReconnection,
  verdict,
  chatRoomId,
  targetChatRoomId,
  profileUserId,
  freshProfileData,
  pxpMetadata,
  pxpTimestamp,
  trigger,
  updateAnonData,
  updateConnectionMetadata,
  locationDataRef,
  locationPromiseRef,
}) => {
  const hostSystemLocation = formatSystemMessageLocation(pxpMetadata);
  if (isReconnection) {
    await applyRepeatConnect({
      kind: verdict.kind,
      chatRoomId: targetChatRoomId,
      profileUserId,
      freshProfileData,
      meetingMetadata: pxpMetadata,
      updateAnonData,
      updateConnectionMetadata,
      locationDataRef,
      locationPromiseRef,
    });
    logMeetingRepeatConnect(targetChatRoomId, {
      kind: verdict.kind,
      minutesSinceMeetingStart: minutesSinceMeetingStart(verdict.existing, pxpTimestamp),
      exchangeType: 'pxp',
      trigger: trigger || null,
    });
    return;
  }
  // First connection — write fresh (original flow)
  if (freshProfileData) {
    await updateAnonData(chatRoomId, freshProfileData);
    console.log('[QR_SCREEN] PXP Host: Saved guest profile data to anonData');
  }
  await saveConnectionMetadata(chatRoomId, pxpMetadata);
  await updateConnectionMetadata(chatRoomId, pxpMetadata);
  console.log(
    `[QR_SCREEN] PXP Host: Metadata saved (location: ${pxpMetadata.location ? 'yes' : 'pending'})`
  );

  if (hostSystemLocation) {
    await persistMessage(
      chatRoomId,
      buildSystemMeetingMessage({
        id: getSystemMessageId(chatRoomId, pxpTimestamp, profileUserId),
        content: hostSystemLocation,
        meetingDate: pxpTimestamp,
        meetingUtcOffset: pxpMetadata.meetingUtcOffset,
      })
    );
  }
};

/** The meeting the host decided, as the reply carries it (and the offer, E3b-2a). */
export const replyMeetingOf = (verdict, pxpMetadata) => ({
  key: verdict.key,
  kind: verdict.kind,
  via: pxpMetadata.via,
  metAt: verdict.metAt,
  metUtcOffset: verdict.metUtcOffset,
});

/**
 * The host's reply: first name + photo (the auto-shared pair) and the meeting
 * verdict the scanner adopts. First meeting: the QR room; re-meet: the room
 * both already share (only participants read a room — 042).
 */
export const sendHostReply = async ({
  profileRef,
  isReconnection,
  chatRoomId,
  targetChatRoomId,
  profileUserId,
  verdict,
  pxpMetadata,
}) => {
  // Fresh read at the payload build (the B20 pattern, extended here at review
  // 2026-08-09): profileRef is an init-time snapshot that survive-close can
  // leave stale — the card sent is the profile as SAVED right now.
  {
    const freshProfile = await getProfileData();
    if (freshProfile) profileRef.current = freshProfile;
  }
  const currentProfile = profileRef.current;
  if (!currentProfile) return currentProfile;
  try {
    // SECURITY: Explicit payload — never spread currentProfile.
    // Full contacts are sent only via SharingSelectionOverlay → sendProfileDataResponse.
    const profileDataToSend = buildProfilePayload(currentProfile, ['name', 'photoUrl'], {
      name: currentProfile.name.split(' ')[0],
      nameSharing: 'first',
    });

    const replyRoomId = isReconnection ? targetChatRoomId : chatRoomId;
    // The verdict the scanner adopts — it never decides alone
    const replyMeeting = replyMeetingOf(verdict, pxpMetadata);
    const reply = () =>
      sendMessage(replyRoomId, {
        id: generateMessageId(),
        type: 'profile_data_response',
        sender: 'profile',
        senderId: getProfileUserId(),
        senderProfileUserId: profileUserId,
        profileData: profileDataToSend,
        connectionMetadata: pxpMetadata,
        meeting: replyMeeting,
        isReconnection,
        timestamp: new Date().toISOString(),
      });
    const result = await reply();
    if (result.error) {
      console.error('[QR_SCREEN] PXP Host: Send failed, retrying once:', result.error);
      await new Promise((r) => setTimeout(r, 1000));
      await reply();
    } else {
      console.log('[QR_SCREEN] PXP Host: Sent profile_data_response to guest - confirmed');
    }

    // Save connectionMetadata locally for sender (first connection only — reconnection already merged)
    if (!isReconnection) {
      await saveConnectionMetadata(chatRoomId, pxpMetadata);
    }
  } catch (error) {
    console.error('[QR_SCREEN] PXP Host: Error sending eager response:', error);
  }
  return currentProfile;
};

/**
 * The scanner's records, adopting the host's meeting verdict: the host's card,
 * the meeting record and banner (first meeting) or the repeat-connect merge.
 * @returns {Promise<Object>} the scanner's meeting record (guestMetadata)
 */
export const writeGuestRecord = async ({
  meeting,
  isReconnection,
  targetChatRoomId,
  hostChatRoomId,
  profileUserId,
  freshProfileData,
  locationDataRef,
  locationPromiseRef,
  updateAnonData,
  updateConnectionMetadata,
}) => {
  const guestTimestamp = new Date().toISOString();
  const { kind } = meeting;
  const guestMetadata = meetingRecordFrom(meeting, {
    exchangeType: 'pxp',
    locationData: locationDataRef.current,
    nowIso: guestTimestamp,
  });

  const guestSystemLocation = formatSystemMessageLocation(guestMetadata);

  if (isReconnection) {
    await applyRepeatConnect({
      kind,
      chatRoomId: targetChatRoomId,
      profileUserId,
      freshProfileData,
      meetingMetadata: guestMetadata,
      updateAnonData,
      updateConnectionMetadata,
      locationDataRef,
      locationPromiseRef,
    });
    return guestMetadata;
  }
  // First connection — write fresh (original flow)
  if (freshProfileData) {
    await updateAnonData(hostChatRoomId, freshProfileData);
  }
  await saveConnectionMetadata(hostChatRoomId, guestMetadata);
  await updateConnectionMetadata(hostChatRoomId, guestMetadata);
  console.log(
    `[QR_SCREEN] PXP Guest: Own metadata saved (location: ${guestMetadata.location ? 'yes' : 'pending'})`
  );

  if (guestSystemLocation) {
    await persistMessage(
      hostChatRoomId,
      buildSystemMeetingMessage({
        id: getSystemMessageId(hostChatRoomId, guestMetadata.meetingDate, profileUserId),
        content: guestSystemLocation,
        meetingDate: guestMetadata.meetingDate,
        meetingUtcOffset: guestMetadata.meetingUtcOffset,
      })
    );
  }
  return guestMetadata;
};

/**
 * The host's card as its reply carries it — first name and photo, the two
 * auto-shared fields (CLAUDE-PROJECT § The sharing model) — read by the scanner
 * from the code's own lookup (get_qr_landing) before anything is recorded
 * (E3b-2a). The host's reply, persisted in the room, carries the same pair and
 * recovery composes from it.
 */
export const hostCardFromLanding = (landing, hostProfileUserId) => ({
  name: firstNameOf(landing?.name) || '',
  photoUrl: landing?.photoUrl || null,
  nameSharing: 'first',
  sharedFields: ['name', 'photoUrl'],
  senderProfileUserId: hostProfileUserId,
});

/**
 * The scanner's knock: its first name + photo into the host's room (the host
 * learns who scanned). senderAnonUserId lets the host detect a connection made
 * before this person converted. Fresh profile read at the build (B20 pattern).
 */
export const sendKnock = async ({
  hostChatRoomId,
  profileUserId,
  profileRef,
  isReconnection,
  trigger,
}) => {
  {
    const freshProfile = await getProfileData();
    if (freshProfile) profileRef.current = freshProfile;
  }
  const currentProfile = profileRef.current;
  if (!currentProfile) return false;
  await sendMessage(hostChatRoomId, {
    type: 'profile_exchange',
    sender: 'profile',
    senderId: getProfileUserId(),
    senderProfileUserId: profileUserId,
    senderAnonUserId: getAnonUserId(),
    chatRoomId: hostChatRoomId,
    profileData: {
      name: currentProfile.name.split(' ')[0],
      photoUrl: currentProfile.photoUrl,
      nameSharing: 'first',
      sharedFields: ['name', 'photoUrl'],
    },
    isReconnection,
    // How this exchange started — the host stamps it on funnel_qr_scanned
    trigger,
    timestamp: new Date().toISOString(),
  });
  console.log('[QR_SCREEN] PXP Guest: Sent profile_exchange to host');
  return true;
};
